import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake, DollarSign, TrendingUp, Shield, Package,
  ArrowUpRight, ArrowDownRight, Plus, Activity, Clock,
  CreditCard, BarChart3, Users, FileText, CheckCircle,
  AlertTriangle, Building2, Wallet, Percent
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function MerchantLendingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('marketplace');
  const [offers, setOffers] = useState([]);
  const [creditLines, setCreditLines] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [earlyPayments, setEarlyPayments] = useState([]);
  const [showCreateOffer, setShowCreateOffer] = useState(false);
  const [newOffer, setNewOffer] = useState({ max_amount: '', min_apr: '', max_apr: '', term_days: 90, min_ci_score: 50, collateral_required: false });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);

      const [offRes, clRes, portRes, epRes] = await Promise.allSettled([
        apiClient.get('/api/v1/finance/m2m/marketplace'),
        apiClient.get('/api/v1/finance/m2m/credit-lines'),
        apiClient.get('/api/v1/finance/m2m/portfolio'),
        apiClient.get('/api/v1/finance/m2m/early-payment/available'),
      ]);

      setOffers(offRes.status === 'fulfilled' ? offRes.value.data?.offers || [] : [
        { offer_id: 'off_001', merchant_id: 'merch_alpha', merchant_name: 'Alpha Electronics', max_amount: 50000, apr_range: { min_apr: 0.06, max_apr: 0.12 }, term_days: 180, min_ci_score: 60, collateral_required: false, status: 'active' },
        { offer_id: 'off_002', merchant_id: 'merch_beta', merchant_name: 'Beta Fashion Co', max_amount: 25000, apr_range: { min_apr: 0.08, max_apr: 0.15 }, term_days: 90, min_ci_score: 70, collateral_required: true, status: 'active' },
        { offer_id: 'off_003', merchant_id: 'merch_gamma', merchant_name: 'Gamma Supplements', max_amount: 100000, apr_range: { min_apr: 0.05, max_apr: 0.10 }, term_days: 365, min_ci_score: 75, collateral_required: false, status: 'active' },
      ]);

      setCreditLines(clRes.status === 'fulfilled' ? clRes.value.data?.credit_lines || [] : [
        { credit_line_id: 'cl_001', lender_name: 'Alpha Electronics', borrower_name: 'My Store', limit: 30000, balance: 12000, apr: 0.08, collateral_type: 'none', status: 'active', created_at: '2026-02-15' },
        { credit_line_id: 'cl_002', lender_name: 'My Store', borrower_name: 'Delta Home Goods', limit: 15000, balance: 5000, apr: 0.10, collateral_type: 'inventory', status: 'active', created_at: '2026-03-01' },
      ]);

      setPortfolio(portRes.status === 'fulfilled' ? portRes.value.data : {
        total_lent: 15000, total_borrowed: 12000, net_position: 3000,
        lending_positions: [{ merchant: 'Delta Home Goods', amount: 15000, balance: 5000, apr: 0.10 }],
        borrowing_positions: [{ merchant: 'Alpha Electronics', amount: 30000, balance: 12000, apr: 0.08 }],
      });

      setEarlyPayments(epRes.status === 'fulfilled' ? epRes.value.data?.offers || [] : [
        { offer_id: 'ep_001', buyer_name: 'Mega Corp', invoice_id: 'INV-2026-0342', invoice_amount: 8500, discount_pct: 2.5, payment_date: '2026-03-25', annualized_return: 18.2, status: 'pending' },
        { offer_id: 'ep_002', buyer_name: 'Prime Retail', invoice_id: 'INV-2026-0355', invoice_amount: 12000, discount_pct: 1.8, payment_date: '2026-03-22', annualized_return: 21.6, status: 'pending' },
      ]);
    } catch (error) {
      console.error('Failed to load M2M data:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const requestCreditLine = async (offerId, amount) => {
    try {
      await apiClient.post('/api/v1/finance/m2m/credit-lines', { offer_id: offerId, amount });
      toast.success('Credit line requested — pending lender approval');
      loadData();
    } catch { toast.error('Failed to request credit line'); }
  };

  const createLoanOffer = async () => {
    try {
      await apiClient.post('/api/v1/finance/m2m/offers', {
        max_amount: parseFloat(newOffer.max_amount),
        apr_range: { min_apr: parseFloat(newOffer.min_apr) / 100, max_apr: parseFloat(newOffer.max_apr) / 100 },
        term_days: newOffer.term_days,
        min_ci_score: newOffer.min_ci_score,
        collateral_required: newOffer.collateral_required,
      });
      toast.success('Loan offer created');
      setShowCreateOffer(false);
      loadData();
    } catch { toast.error('Failed to create offer'); }
  };

  const acceptEarlyPayment = async (offerId) => {
    try {
      await apiClient.post(`/api/v1/finance/m2m/early-payment/${offerId}/accept`);
      toast.success('Early payment accepted — settlement processing');
      loadData();
    } catch { toast.error('Failed to accept early payment'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5CFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      {/* Header */}
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Merchant Lending</h1>
            <p className="text-sm text-[#9CB0C7]">Merchant-to-Merchant Lending</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard/funding')} className="text-sm text-[#9CB0C7] hover:text-white">Funding</button>
            <button onClick={() => navigate('/dashboard')} className="text-sm text-[#9CB0C7] hover:text-white">Dashboard</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Portfolio KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Lent', value: `$${portfolio?.total_lent?.toLocaleString() || 0}`, icon: ArrowUpRight, color: 'text-[#3EF1C7]' },
            { label: 'Total Borrowed', value: `$${portfolio?.total_borrowed?.toLocaleString() || 0}`, icon: ArrowDownRight, color: 'text-[#FFC857]' },
            { label: 'Net Position', value: `$${portfolio?.net_position?.toLocaleString() || 0}`, icon: Wallet, color: portfolio?.net_position >= 0 ? 'text-[#3EF1C7]' : 'text-[#F55454]' },
            { label: 'Active Lines', value: creditLines.filter(c => c.status === 'active').length, icon: CreditCard, color: 'text-[#7A5CFF]' },
          ].map((kpi, i) => (
            <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                <span className="text-xs text-[#9CB0C7]">{kpi.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0E1426] border border-[#22304A] rounded-lg p-1 w-fit">
          {['marketplace', 'credit-lines', 'early-payments', 'portfolio'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab ? 'bg-[#7A5CFF] text-white' : 'text-[#9CB0C7] hover:text-white'}`}>
              {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </button>
          ))}
        </div>

        {/* Marketplace Tab */}
        {activeTab === 'marketplace' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Lending Marketplace</h3>
              <button onClick={() => setShowCreateOffer(true)} className="flex items-center gap-1 px-4 py-2 bg-[#7A5CFF] text-white text-sm rounded-lg hover:bg-[#6B4FE0]">
                <Plus className="w-4 h-4" />Offer to Lend
              </button>
            </div>

            {showCreateOffer && (
              <div className="bg-[#0E1426] border border-[#7A5CFF]/30 rounded-xl p-6 mb-6">
                <h4 className="text-white font-medium mb-4">Create Loan Offer</h4>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-xs text-[#9CB0C7] block mb-1">Max Amount ($)</label>
                    <input type="number" value={newOffer.max_amount} onChange={e => setNewOffer(p => ({ ...p, max_amount: e.target.value }))}
                      className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" placeholder="50000" />
                  </div>
                  <div>
                    <label className="text-xs text-[#9CB0C7] block mb-1">Min APR (%)</label>
                    <input type="number" value={newOffer.min_apr} onChange={e => setNewOffer(p => ({ ...p, min_apr: e.target.value }))}
                      className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" placeholder="5" />
                  </div>
                  <div>
                    <label className="text-xs text-[#9CB0C7] block mb-1">Max APR (%)</label>
                    <input type="number" value={newOffer.max_apr} onChange={e => setNewOffer(p => ({ ...p, max_apr: e.target.value }))}
                      className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" placeholder="12" />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-xs text-[#9CB0C7] block mb-1">Term (days)</label>
                    <input type="number" value={newOffer.term_days} onChange={e => setNewOffer(p => ({ ...p, term_days: parseInt(e.target.value) }))}
                      className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#9CB0C7] block mb-1">Min CI Score</label>
                    <input type="number" value={newOffer.min_ci_score} onChange={e => setNewOffer(p => ({ ...p, min_ci_score: parseInt(e.target.value) }))}
                      className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-[#9CB0C7] cursor-pointer">
                      <input type="checkbox" checked={newOffer.collateral_required}
                        onChange={e => setNewOffer(p => ({ ...p, collateral_required: e.target.checked }))}
                        className="rounded border-[#22304A]" />
                      Require collateral
                    </label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={createLoanOffer} className="px-4 py-2 bg-[#7A5CFF] text-white text-sm rounded-lg hover:bg-[#6B4FE0]">Create Offer</button>
                  <button onClick={() => setShowCreateOffer(false)} className="px-4 py-2 bg-[#22304A] text-[#9CB0C7] text-sm rounded-lg hover:text-white">Cancel</button>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {offers.map((offer, i) => (
                <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-5 hover:border-[#7A5CFF]/30 transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-[#7A5CFF]" />
                      <span className="text-sm font-medium text-white">{offer.merchant_name || offer.merchant_id}</span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#3EF1C7]/20 text-[#3EF1C7]">{offer.status}</span>
                  </div>
                  <div className="space-y-2 text-xs text-[#9CB0C7] mb-4">
                    <div className="flex justify-between"><span>Max Amount</span><span className="text-white font-medium">${offer.max_amount?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>APR Range</span><span className="text-white">{(offer.apr_range?.min_apr * 100).toFixed(0)}% – {(offer.apr_range?.max_apr * 100).toFixed(0)}%</span></div>
                    <div className="flex justify-between"><span>Term</span><span className="text-white">{offer.term_days} days</span></div>
                    <div className="flex justify-between"><span>Min CI Score</span><span className="text-white">{offer.min_ci_score}</span></div>
                    {offer.collateral_required && (
                      <div className="flex items-center gap-1 text-[#FFC857]"><Shield className="w-3 h-3" />Collateral required</div>
                    )}
                  </div>
                  <button onClick={() => requestCreditLine(offer.offer_id, offer.max_amount / 2)}
                    className="w-full py-2 bg-[#7A5CFF]/20 text-[#7A5CFF] text-sm rounded-lg hover:bg-[#7A5CFF]/30">
                    Request Credit Line
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Credit Lines Tab */}
        {activeTab === 'credit-lines' && (
          <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Active Credit Lines</h3>
            <div className="space-y-4">
              {creditLines.map((cl, i) => (
                <div key={i} className="p-4 bg-[#0B1120] rounded-lg border border-[#22304A]">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium text-white">{cl.lender_name} → {cl.borrower_name}</span>
                      <span className={`ml-3 text-xs px-2 py-0.5 rounded-full ${cl.status === 'active' ? 'bg-[#3EF1C7]/20 text-[#3EF1C7]' : 'bg-[#FFC857]/20 text-[#FFC857]'}`}>{cl.status}</span>
                    </div>
                    <span className="text-xs text-[#9CB0C7]">{cl.created_at}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-xs">
                    <div><span className="text-[#9CB0C7] block">Limit</span><span className="text-white font-medium">${cl.limit?.toLocaleString()}</span></div>
                    <div><span className="text-[#9CB0C7] block">Balance</span><span className="text-white font-medium">${cl.balance?.toLocaleString()}</span></div>
                    <div><span className="text-[#9CB0C7] block">APR</span><span className="text-white font-medium">{(cl.apr * 100).toFixed(1)}%</span></div>
                    <div><span className="text-[#9CB0C7] block">Collateral</span><span className="text-white font-medium capitalize">{cl.collateral_type || 'none'}</span></div>
                  </div>
                  <div className="mt-3 w-full bg-[#22304A] rounded-full h-2">
                    <div className="bg-[#7A5CFF] rounded-full h-2" style={{ width: `${(cl.balance / cl.limit) * 100}%` }} />
                  </div>
                  <p className="text-xs text-[#9CB0C7] mt-1">{Math.round((cl.balance / cl.limit) * 100)}% utilized</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Early Payments Tab (C2FO-style) */}
        {activeTab === 'early-payments' && (
          <div>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#7A5CFF]" />Early Payment Marketplace
              </h3>
              <p className="text-sm text-[#9CB0C7] mb-4">Accept early payment offers from buyers for a discount — C2FO-style dynamic discounting.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {earlyPayments.map((ep, i) => (
                <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-white">{ep.buyer_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFC857]/20 text-[#FFC857]">{ep.status}</span>
                  </div>
                  <div className="space-y-2 text-xs text-[#9CB0C7] mb-4">
                    <div className="flex justify-between"><span>Invoice</span><span className="text-white">{ep.invoice_id}</span></div>
                    <div className="flex justify-between"><span>Amount</span><span className="text-white font-medium">${ep.invoice_amount?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>Discount</span><span className="text-[#FFC857]">{ep.discount_pct}%</span></div>
                    <div className="flex justify-between"><span>You Receive</span><span className="text-[#3EF1C7] font-medium">${(ep.invoice_amount * (1 - ep.discount_pct / 100)).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Payment Date</span><span className="text-white">{ep.payment_date}</span></div>
                    <div className="flex justify-between"><span>Annualized Return (Buyer)</span><span className="text-white">{ep.annualized_return}%</span></div>
                  </div>
                  <button onClick={() => acceptEarlyPayment(ep.offer_id)}
                    className="w-full py-2 bg-[#3EF1C7]/20 text-[#3EF1C7] text-sm rounded-lg hover:bg-[#3EF1C7]/30">
                    Accept Early Payment
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && portfolio && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5 text-[#3EF1C7]" />Lending Positions
              </h3>
              {portfolio.lending_positions?.length ? portfolio.lending_positions.map((pos, i) => (
                <div key={i} className="p-3 bg-[#0B1120] rounded-lg mb-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white">{pos.merchant}</span>
                    <span className="text-[#3EF1C7]">${pos.balance?.toLocaleString()} / ${pos.amount?.toLocaleString()}</span>
                  </div>
                  <span className="text-xs text-[#9CB0C7]">{(pos.apr * 100).toFixed(1)}% APR</span>
                </div>
              )) : <p className="text-sm text-[#9CB0C7]">No lending positions</p>}
            </div>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ArrowDownRight className="w-5 h-5 text-[#FFC857]" />Borrowing Positions
              </h3>
              {portfolio.borrowing_positions?.length ? portfolio.borrowing_positions.map((pos, i) => (
                <div key={i} className="p-3 bg-[#0B1120] rounded-lg mb-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white">{pos.merchant}</span>
                    <span className="text-[#FFC857]">${pos.balance?.toLocaleString()} / ${pos.amount?.toLocaleString()}</span>
                  </div>
                  <span className="text-xs text-[#9CB0C7]">{(pos.apr * 100).toFixed(1)}% APR</span>
                </div>
              )) : <p className="text-sm text-[#9CB0C7]">No borrowing positions</p>}
            </div>
          </div>
        )}

        {/* OpenClaw Commands */}
        <div className="mt-8 bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Handshake className="w-5 h-5 text-[#7A5CFF]" />OpenClaw Commands (Mac Mini)
          </h3>
          <p className="text-xs text-[#9CB0C7] mb-4">Manage M2M lending from any messaging channel connected to your Mac mini.</p>
          <div className="grid md:grid-cols-2 gap-2 text-xs font-mono">
            {[
              ['/m2m', 'M2M lending overview + portfolio'],
              ['/m2m offers', 'Browse lending marketplace'],
              ['/m2m lend <amount> <apr> <term>', 'Create a loan offer'],
              ['/m2m borrow <offer_id> <amount>', 'Request credit line'],
              ['/m2m portfolio', 'View full portfolio'],
              ['/m2m early-pay', 'View early payment offers'],
              ['/m2m accept <offer_id>', 'Accept early payment'],
            ].map(([cmd, desc], i) => (
              <div key={i} className="flex gap-3 p-2 bg-[#0B1120] rounded">
                <span className="text-[#7A5CFF] whitespace-nowrap">{cmd}</span>
                <span className="text-[#9CB0C7]">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
